// Toast notification utility using Bootstrap 5 toasts

export type ToastType = 'success' | 'error' | 'info' | 'warning';

interface ToastOptions {
  title?: string;
  message: string;
  type?: ToastType;
  duration?: number;
  link?: {
    text: string;
    url: string;
  };
}

export function showToast(options: ToastOptions) {
  const {
    title,
    message,
    type = 'info',
    duration = 5000,
    link
  } = options;

  // Create toast container if it doesn't exist
  let container = document.getElementById('toast-container');
  if (!container) {
    container = document.createElement('div');
    container.id = 'toast-container';
    container.className = 'toast-container position-fixed top-0 end-0 p-3';
    container.style.zIndex = '9999';
    document.body.appendChild(container);
  }

  // Create toast element
  const toastId = `toast-${Date.now()}`;
  const toast = document.createElement('div');
  toast.id = toastId;
  toast.className = 'toast';
  toast.setAttribute('role', 'alert');
  toast.setAttribute('aria-live', 'assertive');
  toast.setAttribute('aria-atomic', 'true');

  // Icon and color based on type - Using Font Awesome and subtle colors
  const typeConfig = {
    success: { icon: 'fas fa-check-circle', iconColor: 'text-success', borderClass: 'border-success' },
    error: { icon: 'fas fa-exclamation-triangle', iconColor: 'text-danger', borderClass: 'border-danger' },
    warning: { icon: 'fas fa-exclamation-circle', iconColor: 'text-warning', borderClass: 'border-warning' },
    info: { icon: 'fas fa-info-circle', iconColor: 'text-info', borderClass: 'border-info' }
  };

  const config = typeConfig[type];

  // Use safe DOM manipulation instead of innerHTML to prevent XSS
  const toastHeader = document.createElement('div');
  toastHeader.className = `toast-header bg-white ${config.borderClass}`;
  toastHeader.style.borderLeft = '4px solid';

  const icon = document.createElement('i');
  icon.className = `${config.icon} ${config.iconColor} me-2`;
  toastHeader.appendChild(icon);

  const titleElement = document.createElement('strong');
  titleElement.className = 'me-auto text-dark';
  titleElement.textContent = title || type.charAt(0).toUpperCase() + type.slice(1);
  toastHeader.appendChild(titleElement);

  const closeButton = document.createElement('button');
  closeButton.type = 'button';
  closeButton.className = 'btn-close';
  closeButton.setAttribute('data-bs-dismiss', 'toast');
  closeButton.setAttribute('aria-label', 'Close');
  closeButton.innerHTML = '&times;'; // Add visible × symbol as fallback
  closeButton.style.fontSize = '1.5rem';
  closeButton.style.fontWeight = 'bold';
  closeButton.style.border = 'none';
  closeButton.style.background = 'transparent';
  closeButton.style.cursor = 'pointer';
  closeButton.style.opacity = '0.5';
  
  // Add manual click handler as fallback
  closeButton.addEventListener('click', () => {
    toast.classList.remove('show');
    setTimeout(() => toast.remove(), 300);
  });
  
  // Hover effect
  closeButton.addEventListener('mouseenter', () => {
    closeButton.style.opacity = '0.75';
  });
  closeButton.addEventListener('mouseleave', () => {
    closeButton.style.opacity = '0.5';
  });
  
  toastHeader.appendChild(closeButton);

  const toastBody = document.createElement('div');
  toastBody.className = 'toast-body bg-white';
  toastBody.textContent = message;

  if (link) {
    const linkDiv = document.createElement('div');
    linkDiv.className = 'mt-2';
    const linkElement = document.createElement('a');
    linkElement.href = link.url;
    linkElement.target = '_blank';
    linkElement.className = 'btn btn-sm btn-primary';
    linkElement.textContent = link.text;
    linkDiv.appendChild(linkElement);
    toastBody.appendChild(linkDiv);
  }

  toast.appendChild(toastHeader);
  toast.appendChild(toastBody);
  container.appendChild(toast);

  // Initialize and show toast
  try {
    const bootstrap = (window as any).bootstrap;
    if (bootstrap && bootstrap.Toast) {
      const bsToast = new bootstrap.Toast(toast, {
        autohide: duration > 0,
        delay: duration
      });
      bsToast.show();

      // Remove toast element after it's hidden
      toast.addEventListener('hidden.bs.toast', () => {
        toast.remove();
      });
    } else {
      // Fallback: show toast without Bootstrap JS
      toast.classList.add('show');
      if (duration > 0) {
        setTimeout(() => {
          toast.classList.remove('show');
          setTimeout(() => toast.remove(), 300);
        }, duration);
      }
    }
  } catch (error) {
    console.error('Error showing toast:', error);
    // Fallback to alert
    alert(message);
  }
}

export function showSuccess(message: string, title?: string, link?: { text: string; url: string }) {
  showToast({ message, title, type: 'success', link });
}

export function showError(message: string, title?: string, link?: { text: string; url: string }, duration?: number) {
  showToast({ message, title, type: 'error', duration: duration ?? 0, link }); // Don't auto-hide errors by default
}

export function showInfo(message: string, title?: string, duration?: number) {
  showToast({ message, title, type: 'info', duration });
}

export function showWarning(message: string, title?: string, duration?: number) {
  showToast({ message, title, type: 'warning', duration });
}
